<template>
    <div class="w-100">
        <v-list
            id="expert-list-user"
            v-model:opened="openedGroups"
            nav
            density="compact"
            class="respeak-expert-list expert-list-container"
            :class="{ 'no-menu-open': !menuState }"
        >
            <CreateExpertButtonComponent />
            <v-list-group
                v-for="expertType in expertOptions"
                :id="`expert-group-${expertType.id}`"
                :key="expertType.title"
                :value="expertType.title"
                class="expert-group"
            >
                <template #activator="{ props }">
                    <v-list-item
                        v-bind="props"
                        :id="`expert-group-${expertType.id}`"
                        :title="$t(`navigation.${expertType.title}`)"
                    >
                        <template #prepend>
                            <FolderIcon v-if="expertType.id === 'user'" :size="18" class="mr-2" />
                            <Share2Icon v-else :size="18" class="mr-2" />
                        </template>
                    </v-list-item>
                </template>

                <template v-if="expertType.experts.length > 0">
                    <v-list-item
                        v-for="(expert, idx) in expertType.experts"
                        :id="`expert-item-${idx}`"
                        :key="expert.expert_id"
                        :active="expert.expert_id === selectedExpert"
                        :ripple="expert.expert_id !== selectedExpert"
                        class="expert-item"
                        active-class="list-item-active-highlight"
                        @click="onSelectItem(expert.expert_id)"
                    >
                        <div
                            class="d-flex flex-row align-center justify-space-between"
                            :class="{
                                'has-menu-open':
                                    menuState && openedMenuExpertID === expert.expert_id,
                            }"
                            style="max-width: 256px"
                        >
                            <div
                                class="d-flex align-center flex-gap-1 flex-shrink-1 overflow-hidden"
                                style="max-width: 138px"
                            >
                                <div>
                                    <v-list-item-title style="z-index: -2; text-overflow: clip">
                                        {{ expert.name }}
                                    </v-list-item-title>
                                    <v-list-item-subtitle v-if="expert.isShared">
                                        <span v-if="expert.isWorkspace">
                                            {{ $t('navigation.workspace_title') }}
                                        </span>
                                        <span v-else-if="expert.isInvited">
                                            {{ $t('common.invited') }}
                                        </span>
                                        <span v-else-if="expert.isGroup">
                                            {{ $t('common.group') }}
                                        </span>
                                        - {{ $t('common.singular_expert') }}
                                    </v-list-item-subtitle>
                                </div>
                                <v-icon v-if="expert.hasActiveProcesses" color="#6b7280" size="14">
                                    loader
                                </v-icon>
                            </div>

                            <div>
                                <v-chip
                                    v-if="expert.isNew && showChips(expert)"
                                    variant="tonal"
                                    size="x-small"
                                    label
                                    class="status-chip ml-2"
                                    color="success"
                                >
                                    <v-tooltip
                                        :text="$t('navigation.new')"
                                        location="right"
                                        content-class="tooltip-style"
                                    >
                                        <template #activator="{ props }">
                                            <SparklesIcon size="12" v-bind="props" />
                                        </template>
                                    </v-tooltip>
                                </v-chip>
                                <v-chip
                                    v-if="!expert.hasKnowledgebase && showChips(expert)"
                                    variant="tonal"
                                    size="x-small"
                                    label
                                    class="status-chip"
                                    color="warning"
                                >
                                    <v-tooltip
                                        :text="$t('navigation.incomplete')"
                                        location="right"
                                        content-class="tooltip-style"
                                    >
                                        <template #activator="{ props }">
                                            <FolderMinusIcon size="12" v-bind="props" />
                                        </template>
                                    </v-tooltip>
                                </v-chip>
                            </div>
                            <div
                                :id="`expert-menu-${idx}`"
                                class="menu-button px-0"
                                :class="{
                                    show:
                                        expert.expert_id === openedMenuExpertID ||
                                        expert.expert_id === selectedExpert,
                                }"
                                @click.stop="
                                    openedMenuExpertID = expert.expert_id;
                                    menuState = true;
                                "
                            >
                                <EllipsisIcon :size="18" />
                            </div>
                            <div :id="`expert-menu-${expert.expert_id}`" class="menu-anchor"></div>
                            <div
                                v-if="showChips(expert)"
                                :style="{
                                    width: hasChips(expert) ? '80px' : '60px',
                                }"
                                class="text-fade-block"
                            />
                        </div>
                    </v-list-item>
                </template>
                <template v-else-if="expertType.id === 'user'">
                    <CreateExpertButtonComponent visual-type="list" button-variant="secondary" />
                </template>
            </v-list-group>
        </v-list>
        <v-menu
            :target="`#expert-menu-${openedMenuExpertID}`"
            :model-value="menuState"
            offset="0"
            @update:model-value="handleMenuStateChange"
        >
            <v-list id="expert-options-menu" nav density="compact" rounded class="shadow-md">
                <v-list-item
                    v-for="(item, idx) in currentMenuOptions"
                    :id="item.id"
                    :key="item.title"
                    :disabled="item.disabled"
                    :base-color="item.color ? item.color : ''"
                    :class="{ 'mb-0': idx === expert_menu_options.length - 1 }"
                    @click="handleItemClick(item)"
                >
                    <div class="d-flex flex-row align-center">
                        <component :is="item.icon" class="mr-2" :size="18" />
                        <v-list-item-title>
                            {{ $t(`navigation.${item.id}`) }}
                        </v-list-item-title>
                        <v-chip
                            v-if="!item.active"
                            variant="tonal"
                            size="x-small"
                            label
                            class="ml-2"
                            color="info"
                        >
                            {{ $t('navigation.soon') }}
                        </v-chip>
                    </div>
                </v-list-item>
            </v-list>
        </v-menu>

        <DialogDelete
            v-model="dialogDelete"
            title="Delete Expert?"
            :item-name="findExpertById(expertIDToDelete)?.name || ''"
            on-deletion-message="Expert was successfully deleted."
            @confirm="delete_expert"
            @cancel="
                dialogDelete = false;
                expertIDToDelete = null;
            "
        />

        <DialogLeave
            v-model="dialogLeave"
            title="Leave Expert?"
            :item-name="findExpertById(expertIDToLeave)?.name || ''"
            on-deletion-message="You have successfully left the expert."
            @confirm="leave_expert"
            @cancel="
                dialogLeave = false;
                expertIDToLeave = null;
            "
        />
    </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { storeToRefs } from 'pinia';
import { useExpertStore } from '@/stores/expert';
import { useUserStore } from '@/stores/user';
import router from '@/router';
import CreateExpertButtonComponent from '@/components/expert/utils/CreateExpertButtonComponent.vue';
import DialogDelete from '@/components/expert/utils/DialogDelete.vue';
import DialogLeave from '@/components/expert/utils/DialogLeave.vue';
import { role_values } from '@/assets/js/constants';

// icons
import {
    BookCopyIcon,
    ChartColumnIcon,
    Trash2Icon,
    LogOutIcon,
    TestTubeDiagonalIcon,
    EllipsisIcon,
    Share2Icon,
    FolderIcon,
    FolderMinusIcon,
    SparklesIcon,
    PlusIcon,
} from 'lucide-vue-next';

const userStore = useUserStore();
const expertStore = useExpertStore();

// Store refs
const { should_show_admin } = storeToRefs(userStore);

// Emits
const emit = defineEmits(['create-expert']);

// Reactive state
const openedMenuExpertID = ref(null);
const dialogDelete = ref(false);
const dialogLeave = ref(false);
const expertIDToDelete = ref(null);
const expertIDToLeave = ref(null);
const menuState = ref(false);
const openedGroups = ref([]);

// Menu options
const expert_menu_options = [
    {
        id: 'manage',
        title: 'Manage experimental-knowledge',
        icon: BookCopyIcon,
        active: true,
    },
    {
        id: 'analytics',
        title: 'Analytics',
        icon: ChartColumnIcon,
        active: true,
        requiredRoleValue: 2,
    },
    {
        id: 'evaluation',
        title: 'Evaluation',
        icon: TestTubeDiagonalIcon,
        active: true,
        superuser: true,
        requiredRoleValue: 2,
    },
    {
        id: 'leave',
        title: 'Leave expert',
        icon: LogOutIcon,
        active: true,
        color: 'error',
    },
    {
        id: 'delete',
        title: 'Delete expert',
        icon: Trash2Icon,
        active: true,
        color: 'red',
        requiredRoleValue: 4,
    },
];

const userExperts = computed(() => {
    return expertStore.dashboard_experts.filter((expert) => !expert.isShared);
});

const sharedExperts = computed(() => {
    return expertStore.dashboard_experts.filter((expert) => expert.isShared);
});

// Computed properties
const expertOptions = computed(() => {
    let ret_obj = [];
    ret_obj.push({ title: 'your_experts', experts: userExperts.value, id: 'user' });
    if (sharedExperts.value.length > 0) {
        ret_obj.push({ title: 'shared_experts', experts: sharedExperts.value, id: 'shared' });
    }
    return ret_obj;
});

const selectedExpert = computed(() => expertStore.loaded_expert?.expert_id);

const currentMenuOptions = computed(() => {
    const openedExpertOption = findExpertById(openedMenuExpertID.value);
    return expert_menu_options
        .map((option) => {
            let disabled =
                !option.active || option.requiredRoleValue > role_values[openedExpertOption?.role];

            return {
                ...option,
                disabled,
            };
        })
        .filter((option) => option.id !== 'leave' || openedExpertOption?.isInvited)
        .filter((option) => !(option.superuser === true && !should_show_admin.value));
});

// Methods
const onSelectItem = async (expertId) => {
    const selectedExpert = findExpertById(expertId);
    expertStore.load_expert_by_id(expertId, false, true);

    if (!selectedExpert.hasKnowledgebase) {
        await router.push('/expert-configurator/' + expertId);
    } else {
        await router.push(`/home`);
    }
};

const handleItemClick = (item) => {
    if (item.id === 'delete') {
        dialogDelete.value = true;
        expertIDToDelete.value = openedMenuExpertID.value;
    } else if (item.id === 'manage') {
        router.push(`/expert-configurator/${openedMenuExpertID.value}`);
    } else if (item.id === 'analytics') {
        router.push(`/expert-analytics/${openedMenuExpertID.value}`);
    } else if (item.id === 'leave') {
        dialogLeave.value = true;
        expertIDToLeave.value = openedMenuExpertID.value;
    } else if (item.id === 'evaluation') {
        router.push(`/expert-evaluation/${openedMenuExpertID.value}`);
    }
};

const handleMenuStateChange = (value) => {
    menuState.value = value;
    if (!value) {
        openedMenuExpertID.value = null;
    }
};

const showChips = (expert) => {
    return (
        expert.expert_id !== selectedExpert.value && expert.expert_id !== openedMenuExpertID.value
    );
};

const findExpertById = (expertId) => {
    return expertStore.dashboard_experts.find((expert) => expert.expert_id === expertId);
};

const hasChips = (expert) => {
    return showChips(expert) && (expert.isNew || !expert.hasKnowledgebase);
};

const delete_expert = async () => {
    try {
        const isCurrentlyLoadedExpert =
            expertStore.loaded_expert?.expert_id === expertIDToDelete.value;
        const isInHomeView = router.currentRoute.value.name === 'home';
        const isInAgentView = router.currentRoute.value.name === 'agent';
        const response = await expertStore.delete_expert(expertIDToDelete.value);
        dialogDelete.value = false;
        if (response.deleted && isCurrentlyLoadedExpert) {
            if (isInHomeView || isInAgentView) {
                expertStore.load_first_expert_with_sources();
            }
            if (router.currentRoute.value.params.expert_id) {
                router.push('/home');
            }
        }
    } catch (error) {
        console.error(error);
    } finally {
        expertIDToDelete.value = null;
    }
};

const leave_expert = async () => {
    try {
        const isCurrentlyLoadedExpert =
            expertStore.loaded_expert?.expert_id === expertIDToLeave.value;
        const isInHomeView = router.currentRoute.value.name === 'Home';

        const response = await expertStore.leave_expert(
            expertIDToLeave.value,
            userStore.get_user_id,
        );
        dialogLeave.value = false;
        if (response.deleted && isCurrentlyLoadedExpert) {
            if (isInHomeView) {
                expertStore.load_first_expert_with_sources();
            }
            router.push('/home');
        }
    } catch (error) {
        console.error(error);
    } finally {
        expertIDToLeave.value = null;
    }
};

// watcher on user experts to open add the your_experts group when a new expert is created
watch(userExperts, (newExperts) => {
    if (newExperts.length > 0) {
        openedGroups.value = ['your_experts'];
    }
});

// Lifecycle hooks
onMounted(async () => {
    // 1. Ensure expert data is loaded if necessary
    if (!expertStore.dashboard_experts.length) {
        try {
            await expertStore.loadData();
        } catch (error) {
            console.error('Failed to load expert data:', error);
        }
    }

    // 2. Wait for the initial navigation to complete
    await router.isReady();

    // 3. Now access the stable current route
    const currentRoute = router.currentRoute.value;

    // 4. Check route and set initially opened group
    if (currentRoute.name === 'home' && currentRoute.params.expert_id) {
        const expertIdFromRoute = currentRoute.params.expert_id;
        const expert = expertStore.dashboard_experts.find(
            (exp) => exp.expert_id === expertIdFromRoute,
        );

        if (expert) {
            // Find the corresponding group title ('your_experts' or 'shared_experts')
            const group = expertOptions.value.find((opt) =>
                opt.experts.some((e) => e.expert_id === expertIdFromRoute),
            );
            if (group) {
                openedGroups.value = [group.title];
            }
        }
    }
});
</script>

<style lang="scss" scoped>
.no-menu-open .expert-item:hover .menu-button {
    display: block;
    opacity: 1;
}

.no-menu-open .expert-item:hover .status-chip {
    display: none;
}

.expert-item.has-menu-open .menu-button {
    display: block;
    opacity: 1;
}

.expert-item.has-menu-open .status-chip {
    display: none;
}

.expert-item:not(.v-list-item--active) {
    position: relative;
}

.menu-button {
    display: none;
    width: 20px;
    height: 20px;
}

.menu-button.show {
    display: block;
}

.status-chip {
    display: flex;
    z-index: 42;
}

.menu-anchor {
    position: absolute;
    top: 30px;
    right: 30px;
    width: 0;
    height: 0;
}

.respeak-expert-list:not(.no-menu-open) .v-list-item--link {
    cursor: default;
}

.text-fade-block {
    position: absolute;
    top: 0;
    right: 0;
    height: 100%;
    background: linear-gradient(to right, rgba(250, 250, 250, 0), rgb(238, 237, 240) 50%);
}

.no-menu-open .expert-item:hover .text-fade-block {
    display: none;
}

:deep(.v-list-group__items) {
    border-left: 2px solid lightgray;
    margin-left: 16px;
    padding-left: 8px;
}

.v-list-group__items .v-list-item {
    padding-inline-start: 10px !important;
}

.expert-group:not(:first-child) {
    margin-top: 8px;
}
</style>
